import { FilterFields } from '@/data/context/BankFiltersContext';
import useAuth from '@/data/hook/useAuth';
import useInfiniteService from '@/data/hook/useInfiniteService';
import {
  activitiesMetaQueryKeys,
  activitiesQueryKeys,
  lessonsMetaQueryKeys,
  lessonsQueryKeys,
} from '@/data/services/querykeys';
import { isSuperAdmin } from '@/functions/auth';
import { ActivityTypeEnum } from '@/models/_deprecated/ActivityElement';
import { bankTypes, enableQuery } from '@/utils/bank';
import { alternate, joinById } from '@/utils/list';
import { mergeQueries } from '@/utils/query';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import {
  ActivityWithMeta,
  Item,
  LessonWithMeta,
} from './CardsFactory/CardsFactory';
import { useBuildFilters } from './FilterBank.hooks';

type UseInfinityProperties =
  | 'isInitialLoading'
  | 'isFetching'
  | 'isFetchingNextPage'
  | 'hasNextPage';

type UseItemsResult<Item> = Pick<
  UseInfiniteQueryResult<Item>,
  UseInfinityProperties
> & {
  results: Item[];
  invalidate: () => Promise<void>;
  fetchNextPage?: () => void;
};

export const useItems = (
  mode: bankTypes,
  filters: FilterFields,
  options?: {
    enabled?: boolean;
  },
): UseItemsResult<Item> => {
  const serviceFilters = useBuildFilters(filters);

  const additionalFilters = {
    questions: {
      questionType: ActivityTypeEnum.QUIZ,
      questionCountMin: 1,
      questionCountMax: 1,
    },
    activities: {},
    lessons: {},
  };

  const lessonFilters = getItemFilters('lessons');
  const activitiesFilters = getItemFilters('activities');
  const questionsFilters = getItemFilters('questions');
  const defaultQueryOptions = {
    keepPreviousData: true,
    ...options,
  };
  const lessonQueryOptions = {
    ...defaultQueryOptions,
    enabled: enableQuery(mode, 'lessons'),
  };
  const activitiesOptions = {
    ...defaultQueryOptions,
    enabled: enableQuery(mode, 'activities'),
  };
  const questionsOptions = {
    ...defaultQueryOptions,
    enabled: enableQuery(mode, 'questions'),
  };

  function getItemFilters(queryMode: Exclude<bankTypes, 'all'>) {
    const ordering = {
      activities: 'scheduledlesson_count',
      lessons: 'klass_count',
      questions: 'scheduledlesson_count',
    };

    const baseFilters = {
      ...serviceFilters,
      ...additionalFilters[queryMode],
      ordering: ordering[queryMode],
    };

    return baseFilters;
  }

  const lessonsQuery = useInfiniteService({
    ...lessonsQueryKeys.list(lessonFilters)._ctx.infinity,
    ...lessonQueryOptions,
  });

  const metadataLessonsQuery = useInfiniteService({
    ...lessonsMetaQueryKeys.list(lessonFilters)._ctx.infinity,
    ...lessonQueryOptions,
  });

  const activitiesQuery = useInfiniteService({
    ...activitiesQueryKeys.list(activitiesFilters)._ctx.infinity,
    ...activitiesOptions,
  });

  const metadataActivitiesQuery = useInfiniteService({
    ...activitiesMetaQueryKeys.list(activitiesFilters)._ctx.infinity,
    ...activitiesOptions,
  });

  const questionsQuery = useInfiniteService({
    ...activitiesQueryKeys.list(questionsFilters)._ctx.infinity,
    ...questionsOptions,
  });

  const metadataQuestionsQuery = useInfiniteService({
    ...activitiesMetaQueryKeys.list(questionsFilters)._ctx.infinity,
    ...questionsOptions,
  });

  const lessonsAndMetadata = mergeQueries<LessonWithMeta>(
    [lessonsQuery, metadataLessonsQuery],
    joinById,
  );

  const activitiesAndMetadata = mergeQueries<ActivityWithMeta>(
    [activitiesQuery, metadataActivitiesQuery],
    joinById,
  );

  const questionsAndMetadata = mergeQueries<ActivityWithMeta>(
    [questionsQuery, metadataQuestionsQuery],
    joinById,
  );

  const allItems = mergeQueries<Item>(
    [lessonsAndMetadata, activitiesAndMetadata],
    alternate,
  );

  const items: Record<bankTypes, UseItemsResult<Item>> = {
    all: allItems,
    lessons: lessonsAndMetadata,
    activities: activitiesAndMetadata,
    questions: questionsAndMetadata,
  };

  return items[mode];
};

export const useShouldShowItem = (item: Item) => {
  const { user } = useAuth();

  const shouldRender = () => {
    if (!item.isPublicInBank)
      return item.author === user?.id || isSuperAdmin(user?.userType);
    return true;
  };

  return shouldRender();
};
